import { requestRaw, buildApiUrl } from "../dataProvider/requestJson";
import { GET_TIMEZONES } from "../constants/endpoints";
import {
  TIMEZONE_OPTIONS,
  decorateTimezoneOption,
} from "../constants/timezones";

/**
 * Client for `GET /restapi/timezones`.
 *
 * The backend list is the source of truth: 604 entries, including tzdb backward-compat
 * links (America/Yellowknife and friends) that `Intl.supportedValuesOf` omits but which
 * exist in our data. constants/timezones.js stays as the offline fallback.
 *
 * Responses carry `Cache-Control: max-age=86400` + ETag, so the browser handles HTTP
 * caching; the in-memory cache below just avoids redundant work as the user edits the
 * country/state fields.
 */

// Keyed by `country|state`. Reference data, so it never needs invalidating in-session.
const cache = new Map();

const cacheKey = (country, state) =>
  `${(country || "").trim().toUpperCase()}|${(state || "").trim().toUpperCase()}`;

/**
 * Returns `{ options, narrowed, failed }`.
 *
 * - `options` — decorated, ready for the picker. NEVER empty: the endpoint fails open,
 *   and a request failure falls back to the local list.
 * - `narrowed` — whether narrowing actually applied, from the `X-Timezones-Narrowed`
 *   header. Drives the "showing suggestions for X" hint and the escape hatch.
 * - `failed` — the request errored and `options` is the local fallback.
 *
 * A narrowed list is a SUGGESTION, never a validity rule. Callers must still union in the
 * stored value (see withTimezoneSelectable) or they reintroduce the substitution bug.
 */
export const fetchTimezones = async ({ country, state } = {}) => {
  const key = cacheKey(country, state);
  if (cache.has(key)) return cache.get(key);

  try {
    const { json, headers } = await requestRaw(
      buildApiUrl(GET_TIMEZONES({ country, state })),
    );

    const entries = Array.isArray(json) ? json : [];
    const options = entries.map(decorateTimezoneOption).filter(Boolean);

    // Defensive: the endpoint is documented never to return empty, but if it somehow
    // does, fall back rather than presenting a picker with nothing in it.
    if (!options.length) {
      const result = { options: TIMEZONE_OPTIONS, narrowed: false, failed: false };
      cache.set(key, result);
      return result;
    }

    const result = {
      options,
      narrowed: headers.get("X-Timezones-Narrowed") === "true",
      failed: false,
    };

    cache.set(key, result);
    return result;
  } catch (error) {
    // Not cached: a transient failure shouldn't pin the fallback for the whole session.
    return { options: TIMEZONE_OPTIONS, narrowed: false, failed: true };
  }
};

// Exported for tests / manual cache reset.
export const clearTimezoneCache = () => cache.clear();
